import React from 'react';

import IconButton from '@material-ui/core/IconButton';
import Button from '@material-ui/core/Button';
import Container from '@material-ui/core/Container';

import Link from '@material-ui/core/Link';
import Typography from '@material-ui/core/Typography';

import Paper from '@material-ui/core/Paper'
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Checkbox from '@material-ui/core/Checkbox';
import Grid from '@material-ui/core/Grid';
import Box from '@material-ui/core/Box';
import { makeStyles } from '@material-ui/core/styles';
import Divider from '@material-ui/core/Divider';
import InputAdornment from "@material-ui/core/InputAdornment";
import VisibilityIcon from '@material-ui/icons/Visibility';
import VisibilityOffIcon from '@material-ui/icons/VisibilityOff';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';

import { useSnackbar } from 'notistack';
import passwordValidator from 'password-validator';
import { useFormik, useField, Form, FormikProvider } from 'formik';

import FullsizeLogo from 'components/Logo/FullsizeLogo';
import TermsOfUse from 'components/Copyright/TermsOfUse';
import PrivacyPolicy from 'components/Copyright/PrivacyPolicy';
import FirebaseAuthContext from 'components/Auth/FirebaseAuthContext';
import useAuth from 'components/Auth/useAuth';
import useClient from 'components/Auth/useClient';
import useFunctions from 'components/Auth/useFunctions';

import { createTheme, ThemeProvider } from '@material-ui/core';

import {
  signInWithEmailAndPassword,
  sendPasswordResetEmail,
  sendEmailVerification,
  signInWithRedirect,
  GoogleAuthProvider,
} from 'firebase/auth';

const useStyles = makeStyles((theme) => ({
  paper: {
    margin: 'auto',
    maxWidth: '420px',
    borderRadius: '10px'
  },
  container: {
    padding: theme.spacing(8, 0),
    textAlign: 'left',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  form: {
    width: '275px', // Fix IE 11 issue.
    marginTop: theme.spacing(1),
  },
  typography: {
    marginLeft: '0px',
    fontSize: '13px',
    whiteSpace: 'nowrap'
  },
  icon: {
    position: 'absolute',
    marginLeft: '-20px',
  },
  textLabel: {
    fontSize: '13px',
    fontWeight: 'bold',
  },
  textfield: {
  },
  checkbox: {
  },
  submit: {
    marginTop: theme.spacing(4),
  },
  buttonOutlined: {
    border: 'solid 2px',
    '&:hover': {
      border: 'solid 2px',
      borderColor: '#18719A',
    },
  },
  blanding: {
    fontSize: '17px',
    fontWeight: 'bold',
    paddingBottom: theme.spacing(1),
  },
  close: {
    alignSelf: "end"
  },
  divider: {
    margin: theme.spacing(5, 0),
    alignItems: "center",
    textAlign: "center"
  },
}));


const passwordLength = 'パスワードは8～30文字以内で入力してください';
const passwordSpace = 'パスワードにスペースを含めないでください';
const passwordCase = '英大文字、英小文字、数字を各1字以上使用してください';
const passwordSchema = new passwordValidator();
passwordSchema
  .is().min(8, passwordLength)
  .is().max(30, passwordLength)
  .has().uppercase(1, passwordCase)
  .has().lowercase(1, passwordCase)
  .has().digits(1, passwordCase)
  .has().not().spaces(1, passwordSpace);

function validatePassword(value) {
  const ret = passwordSchema.validate(value, { details: true });
  if (ret.length > 0) {
    return ret[0].message;
  }
};


const FormText = React.forwardRef(function FormText(props, ref) {
  const classes = useStyles();
  return (
    <div>
      <Typography ref={ref} className={classes.typography}>{props.children}</Typography>
    </div>
  )
});

import OutlinedInput from '@material-ui/core/OutlinedInput';
import FormHelperText from '@material-ui/core/FormHelperText';
const FormTextField = React.forwardRef(function FormTextField(props, ref) {
  const [field, meta] = useField(props);

  let
    {
      label,
      helperText,
      emphasisHelp,
      ...rest
    } = props;

  const error = Boolean(meta.error);
  if (error) {
    helperText = meta.error;
  }

  const classes = useStyles();
  return (
    <div ref={ref} style={{marginTop:'10px'}}>
      <div><Typography className={classes.textLabel}>{label}</Typography></div>
      <div>
      <OutlinedInput
        ref={ref}
        className={classes.textField}
        variant="outlined"
        required
        fullWidth
        error={meta.touched && error}
        
        {...field}
        {...rest} 
        /></div>
        {
          emphasisHelp ? 
          <div style={{display: 'flex', alignItems: 'center', flexWrap: 'nowrap'}}><ErrorOutlineIcon className={classes.icon} fontSize="small" color="secondary" /><FormHelperText error={meta.touched && error} className={classes.typography}>{helperText}</FormHelperText></div> :
          <div><FormHelperText error={meta.touched && error} className={classes.typography}>{helperText}</FormHelperText></div>
        }
    </div>
  )
});

const FormPassword = React.forwardRef(function FormPassword(props, ref)
{
  let
  {
    label,
    validate,
    helperText,
    ...rest
  } = props;

  const [field, meta] = useField({ ...props, validate: validate ? validatePassword : null });

  const error = Boolean(meta.error);
  if (error) {
    helperText = meta.error;
  }

  const classes = useStyles();
  const [isVisible, setIsVisible] = React.useState(false);
  const onVisibilityChange = React.useCallback((visible) =>
  {
    setIsVisible(visible);
  }, []);

  return (
    <div ref={ref} style={{marginTop:'10px'}}>
      <div><Typography className={classes.textLabel}>{label}</Typography></div>
      <div><OutlinedInput
        fullWidth
        className={classes.textField}
        {...field}
        {...rest}
        type={isVisible ? "text" : "password"}
        endAdornment={
            <InputAdornment position="end">
              <IconButton
                aria-label="Toggle password visibility"
                onClick={() => {
                  onVisibilityChange(!isVisible);
                }}
                onMouseDown={(event) => {
                  event.preventDefault();
                }}
                size="medium">
                {isVisible ? <VisibilityIcon /> : <VisibilityOffIcon />}
              </IconButton>
            </InputAdornment>
        }
      /></div>
      <div><FormHelperText error={meta.touched && error} className={classes.typography}>{helperText}</FormHelperText></div>
   </div>
  );
});

const FormCheckbox = React.forwardRef(function FormCheckbox(props, ref) {
  const [field, meta, helpers] = useField(props);

  let {
    label,
    className,
    ...rest
  } = props;

  let {
    value,
    ...field_rest
  } = field

  const classes = useStyles();
  return (
    <FormControlLabel 
      label={label}
      className={classes.checkbox}
      control={
      <Checkbox
        ref={ref}
        style={{height:'28px'}}
        disableRipple
        checked={field.value}
        color="primary"
        {...field_rest}
        {...rest}
         />}
      />)
});

function RegistrationForm(props, ref)
{
  const {
    setRegistration,
    setRegistrationComplete
  } = props;

  const { enqueueSnackbar } = useSnackbar();

  const auth = useAuth();
  const client = useClient();
  const fbAuth = React.useContext(FirebaseAuthContext);
  const handleRegister = React.useCallback((values) => {
    client
      .post(`api/user/register`, { ...values, redirectUrl:window.location.href} )
      .then((response) => signInWithEmailAndPassword(fbAuth, values.email, values.password))
      .catch((error) => {
        if (error?.response?.status == 409) {
          enqueueSnackbar('アカウントは既に登録されています。', { variant: 'error' });
        }
        else {
          enqueueSnackbar('アカウントを登録できませんでした。', { variant: 'error' });
        }
      });
  }, []);

  const formikBag = useFormik(
    {
      initialValues: {
        email: auth.email ?? '',
        name: auth.name ?? '',
        password: '',
        agreeToTerm: false,
        agreeToAge: false,
      },
      onSubmit: handleRegister
    });

  const classes = useStyles();
  return (
    <React.Fragment>
      <FormikProvider value={formikBag}>
      <Form className={classes.form}>
        <FormTextField
          variant="outlined"
          required
          fullWidth
          id="name"
          label="氏名"
          name="name"
          autoComplete="name"
          autoFocus
        />
        <Box m={2} />
        <FormTextField
          id="email"
          label="メールアドレス"
          name="email"
          autoComplete="email"
          variant="outlined"
          helperText="URLを受信したメールアドレスを入力してください"
          emphasisHelp
          required
        />
        <FormPassword
          name="password"
          label="パスワード"
          variant="outlined"
          helperText={passwordLength}
          required
          validate
        />
        <Box m={3}/>
        <FormCheckbox
          label={<FormText><TermsOfUse target="_blank" underline="always"/>と<PrivacyPolicy target="_blank" underline="always"/>に同意する</FormText>}
          id="agreeToTerm"
          name="agreeToTerm"
                />
        <FormCheckbox
          label={<FormText>18歳未満は、保護者の同意を得ている</FormText>}
          id="agreeToAge"
          name="agreeToAge"
                />
        <Button
          variant="contained"
          color="primary"
          type="submit"
          className={classes.submit}
          fullWidth
          disabled={!formikBag.isValid || !formikBag.values.agreeToTerm || !formikBag.values.agreeToAge}>
          アカウントを新規登録する
        </Button>
        <Box m={6} />
        <Button 
          fullWidth
          variant="outlined"
          color="primary"
          className={classes.buttonOutlined}
          onClick={() => setRegistration(false)}>
          既にアカウントをお持ちの方
        </Button>
      </Form>
    </FormikProvider>      
    </React.Fragment>);
}

function LoginForm(props) {
  const {
    setPasswordReset,
    setRegistration
  } = props;

  const { enqueueSnackbar } = useSnackbar();

  const fbAuth = React.useContext(FirebaseAuthContext);
  const functions = useFunctions();
  const handleLogin = React.useCallback((values) => {
    signInWithEmailAndPassword(fbAuth, values.email, values.password)
      .then(user => {
        enqueueSnackbar('ログインに成功しました。', { variant: 'success' });
        functions.removeToken();
      })
      .catch(error => {
        enqueueSnackbar('ログインに失敗しました。\nメールアドレスかパスワードに誤りがあります。', { variant: 'error', style: { whiteSpace: 'pre-line' } });
      })
  }, []);

  const formikBag = useFormik(
    {
      initialValues: {
        email: '',
        password: ''
      },
      onSubmit: handleLogin
    });

  const classes = useStyles();
  return (
    <FormikProvider value={formikBag}>
      <Form className={classes.form}>
        <FormTextField
          id="email"
          label="メールアドレス"
          name="email"
          autoComplete="email"
          autoFocus
        />
        <FormText>URLを受信したメールアドレスを入力してください</FormText>
        <FormPassword
          variant="outlined"
          required
          fullWidth
          name="password"
          label="パスワード"
          type="password"
          id="password"
          autoComplete="current-password"
          style={{width:"275px"}}
        />
        <Link variant="body2" className={classes.typography} onClick={() => setPasswordReset(true)}>
          {"パスワードを忘れた方はこちら"}
        </Link>
        <Button
          type="submit"
          fullWidth
          variant="contained"
          color="primary"
          className={classes.submit}
        >
          ログインする
        </Button>
        <Grid container className={classes.divider}>
          <Grid item xs={4}>
            <Divider />
          </Grid>
          <Grid item xs={4}>
            <Typography color="textSecondary">または</Typography>
          </Grid>
          <Grid item xs={4}>
            <Divider />
          </Grid>
        </Grid>
        <Button 
          fullWidth
          variant="outlined"
          color="primary"
          className={classes.buttonOutlined}
          onClick={() => setRegistration(true)}>
          アカウントを新規登録する
        </Button>
      </Form>
    </FormikProvider>);
}

function PasswordResetForm(props) {
  const { enqueueSnackbar } = useSnackbar();

  const {
    setPasswordReset,
    setPasswordResetComplete,
  } = props;

  const client = useClient();
  const handleReset = React.useCallback((values) => {
    client
      .post(`api/user/sendPasswordResetLink`, { ...values, redirectUrl:window.location.href} )
      .then(() => {
        setPasswordResetComplete(true);
        enqueueSnackbar('メールの送信が完了しました。\nメールのリンクから30分以内に手続きを完了して下さい。', { style: { whiteSpace: 'pre-line' } });
      })
      .catch((error) => {
        if (error.response.status == 409) {
          enqueueSnackbar('再設定メール送信の頻度が高すぎます。時間をおいて再度実行してください。', { variant: 'error' });
        }
        else {
          enqueueSnackbar('メールが送信できませんでした。', { variant: 'error' });
        }
      });
  }, []);

  const formikBag = useFormik(
    {
      initialValues: {
        email: ''
      },
      onSubmit: handleReset
    });

  const classes = useStyles();
  return (
    <FormikProvider value={formikBag}>
      <Form className={classes.form}>
        <FormTextField
          variant="outlined"
          required
          fullWidth
          id="email"
          label="メールアドレス"
          name="email"
          autoComplete="email"
          autoFocus
        />
        <Button
          type="submit"
          fullWidth
          variant="contained"
          color="primary"
          className={classes.submit}
        >
          パスワードの再設定メールを送信する
        </Button>
        <Box m={3} />
        <Button 
          fullWidth
          variant="outlined"
          color="primary"
          className={classes.buttonOutlined}
          onClick={() => setPasswordReset(false)}>
          ログインに戻る
        </Button>
      </Form>
    </FormikProvider>);
}

function CompleteForm(props)
{
  const {
    message
  } = props;
  return (<React.Fragment>
    <Typography>{message}</Typography>
    <Typography>※届くまで数分かかる場合があります。</Typography>
    <p></p>
    <Typography>メールに記載のURLから手続きを完了してください。</Typography>
  </React.Fragment>);
}

const theme = createTheme({
  palette: {
    primary: {
      main: "#1F91C9"
    },
    secondary: {
      main: "#FF0000"
    },
    background: {
      default: "#FFFFFF"
    },
    text: {
      primary: '#3B4043',
      secondary: '#3B4043'
    },
  },
  typography: {
    fontFamily: [
      'Roboto',
      'Noto Sans JP',
      'sans-serif',
    ].join(','),

    button: {
      fontWeight: 700,
    },    
  },
});

import Fade from '@material-ui/core/Fade';
import { SwitchTransition } from 'react-transition-group'

export default function PaperLogin(props) {
  const [passwordReset, setPasswordReset] = React.useState(false);
  const [passwordResetComplete, setPasswordResetComplete] = React.useState(false);
  const [registration, setRegistration] = React.useState(true);
  const [registrationComplete, setRegistrationComplete] = React.useState(false);
  
  const component = React.useMemo(() => {
    if (passwordResetComplete) return <CompleteForm message="パスワード更新手続きのメールを送信しました。"/>;
    if (passwordReset) return <PasswordResetForm setPasswordReset={setPasswordReset} setPasswordResetComplete={setPasswordResetComplete}/>;
    if (registrationComplete) return <CompleteForm message="アカウント登録の確認メールを送信しました。"/>;
    return (registration) ?
      <RegistrationForm
        setRegistration={setRegistration}
        setRegistrationComplete={setRegistrationComplete}
        /> :
      <LoginForm
        setPasswordReset={setPasswordReset}
        setRegistration={setRegistration}
        />;
  }, [passwordResetComplete, passwordReset, registrationComplete,registration]);

  const classes = useStyles();

  const key = registration ? "registration" : passwordReset ? "passwordReset" : "login";

  return (
      <ThemeProvider theme={theme}>
        <Paper className={classes.paper}>
          <Container component="main" maxWidth="xs" className={classes.container}>
            <Typography className={classes.blanding} color="textSecondary">クラウド帳票サービス</Typography>
            <FullsizeLogo />
            <Box m={2} />
            <SwitchTransition>
              <Fade key={key} in={true}
                >
                <div>
                  {component}
                </div>
              </Fade>
            </SwitchTransition>
          </Container>
        </Paper>
      </ThemeProvider>
);
}